/**
 * ScreenLayout.ts — Viewport management and responsive sizing.
 *
 * Tracks terminal dimensions and provides layout constants
 * for scene rendering (status bar height, content area, menu area, etc.)
 *
 * Shared across ChoiceRealms titles (Ascend, Gallowcrest).
 */

import type { Terminal } from './Terminal';

export interface LayoutRegion {
  startRow: number;
  endRow: number;
  height: number;
}

export interface Layout {
  cols: number;
  rows: number;
  statusBar: LayoutRegion;
  content: LayoutRegion;
  menu: LayoutRegion;
  inputPrompt: LayoutRegion;
}

/** Minimum terminal dimensions */
export const MIN_COLS = 80;
export const MIN_ROWS = 24;

/** Fixed row allocations */
const STATUS_BAR_HEIGHT = 2;
const INPUT_PROMPT_HEIGHT = 2;

export class ScreenLayout {
  private terminal: Terminal;
  private layout: Layout;
  private onLayoutChangeHandlers: Array<(layout: Layout) => void> = [];

  constructor(terminal: Terminal) {
    this.terminal = terminal;
    this.layout = this.calculateLayout();

    this.terminal.onResize(() => {
      this.layout = this.calculateLayout();
      for (const handler of this.onLayoutChangeHandlers) {
        handler(this.layout);
      }
    });
  }

  /** Get the current layout */
  getLayout(): Layout {
    return this.layout;
  }

  /** Register a handler for layout changes (window resize) */
  onLayoutChange(handler: (layout: Layout) => void): void {
    this.onLayoutChangeHandlers.push(handler);
  }

  /** Remove a layout change handler */
  offLayoutChange(handler: (layout: Layout) => void): void {
    this.onLayoutChangeHandlers = this.onLayoutChangeHandlers.filter(h => h !== handler);
  }

  /** Check if terminal meets minimum size requirements */
  isSizeSufficient(): boolean {
    const { cols, rows } = this.terminal.getSize();
    return cols >= MIN_COLS && rows >= MIN_ROWS;
  }

  /** Calculate layout regions based on current terminal dimensions */
  private calculateLayout(): Layout {
    const { cols, rows } = this.terminal.getSize();

    const statusBar: LayoutRegion = {
      startRow: 1,
      endRow: STATUS_BAR_HEIGHT,
      height: STATUS_BAR_HEIGHT,
    };

    const inputPrompt: LayoutRegion = {
      startRow: rows - INPUT_PROMPT_HEIGHT + 1,
      endRow: rows,
      height: INPUT_PROMPT_HEIGHT,
    };

    const middleStart = STATUS_BAR_HEIGHT + 1;
    const middleEnd = rows - INPUT_PROMPT_HEIGHT;
    const middleHeight = middleEnd - middleStart + 1;

    const menuHeight = Math.min(Math.floor(middleHeight * 0.4), 15);
    const contentHeight = middleHeight - menuHeight;

    const content: LayoutRegion = {
      startRow: middleStart,
      endRow: middleStart + contentHeight - 1,
      height: contentHeight,
    };

    const menu: LayoutRegion = {
      startRow: middleStart + contentHeight,
      endRow: middleEnd,
      height: menuHeight,
    };

    return {
      cols,
      rows,
      statusBar,
      content,
      menu,
      inputPrompt,
    };
  }
}
